// @ts-nocheck
import { getChartConfig } from "app/pages/website-analysis/incoming-traffic/chartConfig";

import "pro-features/components/Chart/styles/sharedTooltip.scss";
import { useEffect, useRef, useState } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Box } from "@similarweb/ui-components";

import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { Legends } from "app/components/React/Legends/Legends";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberVisitsFilter, minVisitsAbbrFilter } from "app/filters/ngFilters";
import { TitleContainer } from "app/pages/website-analysis/social-overview/social-total-visits/StyledComponents";
import DurationService from "app/services/DurationService";
import { PngExportService } from "app/services/PngExportService";
import { allTrackers } from "app/services/track/track";
import Chart from "pro-features/components/Chart/src/Chart";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";

const StyledTitleContainer = styled(TitleContainer)`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StyledBox = styled(Box)<{ isCompare?: boolean }>`
    width: 100%;
    height: ${({ isCompare }) => (isCompare ? 450 : 370)}px;
    display: flex;
    flex-direction: column;
`;
const LegendsContainer = styled.div`
    padding-left: 24px;
`;

const Seperator = styled.hr`
    margin: 0 0 28px 0;
`;

const createLegends = (rawData) => {
    let winner;
    let winnerVisits = 0;
    const colors = CHART_COLORS.compareMainColors.slice();
    const legendItems = Object.entries<{ Volumes: Array<Array<number>> }>(rawData)
        .map(([domain, data], index) => {
            const domainVisits = data.Volumes.reduce((acc, current) => {
                return acc + (current[0] || 0) + (current[1] || 0);
            }, 0);
            if (domainVisits > winnerVisits) {
                winnerVisits = domainVisits;
                winner = domain;
            }
            return {
                name: domain,
                rawName: domain,
                color: colors[index],
                visible: true,
                isDisabled: false,
                data: minVisitsAbbrFilter()(domainVisits),
                domainVisits,
                isWinner: false,
            };
        })
        .filter(({ name, domainVisits }) => rawData[name].Volumes.length > 0 && domainVisits > 0);
    legendItems.forEach((legend) => {
        if (legend.name === winner) {
            legend.isWinner = true;
        }
    });
    return legendItems;
};
export const SocialOverviewLineChart = ({ webSource, from, loading, error, rawData, isCompare, duration }) => {
    const i18n = i18nFilter();
    const lineChartInstance = useRef(null);
    const [legendItems, setLegendItems] = useState([]);
    useEffect(() => {
        if (rawData && !loading) {
            setLegendItems(createLegends(rawData));
        }
    }, [rawData, loading]);
    const title = i18n(`social.traffic.over.time`);
    const tooltip = i18n("social.traffic.over.time.tooltip");
    const afterLineChartRender = (chart) => {
        lineChartInstance.current = chart;
    };
    const pngDownload = (): Promise<void> => {
        return new Promise((resolve) => {
            allTrackers.trackEvent(`Download`, `submit-ok`, `Social visits/PNG`);
            if (lineChartInstance.current) {
                let trimmedDateTitle = title.indexOf("from") != -1 ? title.substr(0, title.indexOf("from") - 1) : title;
                trimmedDateTitle =
                    title.indexOf("Last") != -1 ? title.substr(0, title.indexOf("Last") - 1) : trimmedDateTitle;
                PngExportService.getInstance().export(lineChartInstance.current, trimmedDateTitle);
                resolve();
                return;
            }
            resolve();
        });
    };
    const colors = isCompare ? CHART_COLORS.compareMainColors.slice() : [colorsPalettes.blue[400]];
    const chartData = Object.entries<{ Dates: string[]; Volumes: Array<Array<string>> }>(rawData || {})
        .map(([domain, domainData], index) => {
            return {
                color: colors[index],
                name: domain,
                tooltipIndex: index,
                marker: { enabled: true, symbol: "circle" },
                data: domainData.Dates.map((date, index) => {
                    return {
                        x: dayjs.utc(date).valueOf(),
                        y: domainData.Volumes[index][0] + domainData.Volumes[index][1],
                    };
                }),
                visible: legendItems[index]?.visible ?? true,
            };
        })
        .filter(({ name }) => rawData[name].Volumes.length > 0);
    const durationObject = DurationService.getDurationData(duration);
    const getDateRange = (durationObject) => [durationObject.raw.from, durationObject.raw.to];
    const [fromDate, toDate] = getDateRange(durationObject);

    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
                useRangeDisplay: fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
            },
        },
        {
            filter: "webSource",
            value: webSource as string,
        },
    ];

    const toggleSeries = (item) => {
        const idx = legendItems.findIndex(({ name }) => {
            return name === item.name;
        });
        const newLegendItems = [...legendItems];
        newLegendItems[idx].visible = !newLegendItems[idx].visible;
        setLegendItems(newLegendItems);
    };
    return (
        <StyledBox isCompare={isCompare}>
            <StyledTitleContainer>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                />
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={pngDownload}
                />
            </StyledTitleContainer>
            {loading ? (
                <WidgetLoader />
            ) : error ? (
                <NoDataLandscape
                    title="global.nodata.notavilable"
                    subtitle={null}
                />
            ) : (
                <>
                    {isCompare && legendItems.length > 0 ? (
                        <>
                            <Seperator />
                            <LegendsContainer>
                                <Legends
                                    legendItems={legendItems}
                                    toggleSeries={toggleSeries}
                                    data-automation="display-ads-graph-legends"
                                />
                            </LegendsContainer>
                        </>
                    ) : null}
                    <Chart
                        type="line"
                        config={getChartConfig({
                            type: "line",
                            filter: [abbrNumberVisitsFilter, 1],
                            isDaily: false,
                            isSingle: true,
                            data: chartData,
                            webSource,
                            from,
                        })}
                        data={chartData}
                        domProps={{ style: { height: "366px" } }}
                        afterRender={afterLineChartRender}
                    />
                </>
            )}
        </StyledBox>
    );
};
